import React, {useEffect, useState} from 'react';
import './GalleryComponent.css';
import ImageDescriptionMapper from './ImageDescriptionMapper';

const GalleryComponent = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [loadedImages, setLoadedImages] = useState(0);

    useEffect(() => {
        // Since the API is not returning the right data, let's create our own image array
        // based on the file names we know exist in the S3 bucket
        const createGalleryImages = () => {
            try {
                setLoading(true);

                // Create an array of images based on the expected Web1.webp through Web16.webp format
                const imagesArray = Array.from({length: 16}, (_, i) => {
                    const index = i + 1;
                    const fileName = `Web${index}.webp`;
                    const title = `Web${index}`;

                    return {
                        id: index,
                        url: `https://www.thesealab.com/gallery/${fileName}`,
                        title: title,
                        description: ImageDescriptionMapper[title] ||
                            "Explore the wonders of marine life at The Sealab."
                    };
                });

                setImages(imagesArray);
                setError(null);
            } catch (err) {
                setError('Failed to load images. Please try again later.');
                console.error('Error creating gallery images:', err);
            } finally {
                setLoading(false);
            }
        };

        createGalleryImages();
    }, []);

    const handleImageLoad = () => {
        setLoadedImages(prev => prev + 1);
    };

    const handleImageError = (index) => {
        console.error(`Failed to load image at index ${index}`);
        // You could potentially update the state to mark this image as failed
    };

    const openLightbox = (image) => {
        setSelectedImage(image);
        document.body.classList.add('gallery-no-scroll');
    };

    const closeLightbox = () => {
        setSelectedImage(null);
        document.body.classList.remove('gallery-no-scroll');
    };

    const navigateImage = (direction) => {
        const currentIndex = images.findIndex(img => img.id === selectedImage.id);
        const newIndex = direction === 'next'
            ? (currentIndex + 1) % images.length
            : (currentIndex - 1 + images.length) % images.length;
        setSelectedImage(images[newIndex]);
    };

    const handleKeyDown = (e) => {
        if (!selectedImage) return;

        if (e.key === 'Escape') {
            closeLightbox();
        } else if (e.key === 'ArrowRight') {
            navigateImage('next');
        } else if (e.key === 'ArrowLeft') {
            navigateImage('prev');
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.classList.remove('gallery-no-scroll');
        };
    }, [selectedImage, images]);

    return (
        <div className="gallery-container min-vh-100">
            <h2 className="gallery-title">The Sealab Photo Gallery</h2>

            {loading ? (
                <div className="gallery-loading">
                    <div className="gallery-spinner"></div>
                    <p className="gallery-loading-text">Loading gallery...</p>
                </div>
            ) : error ? (
                <div className="gallery-error">
                    <p className="gallery-error-text">{error}</p>
                    <button
                        className="gallery-retry-button btn btn-dark"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            ) : (
                <>
                    {/*<div className="gallery-filter-bar">*/}
                    {/*    <p className="gallery-count">{images.length} images</p>*/}
                    {/*</div>*/}

                    <div className="gallery-grid">
                        {images.map((image, index) => (
                            <div key={image.id} className="gallery-item-container">
                                <div
                                    className="gallery-item"
                                    onClick={() => openLightbox(image)}
                                >
                                    <div className="gallery-image-wrapper">
                                        <img
                                            src={image.url}
                                            alt={image.title}
                                            className="gallery-image"
                                            onLoad={handleImageLoad}
                                            onError={() => handleImageError(index)}
                                            loading="lazy"
                                        />
                                        {/*<div className="gallery-overlay">*/}
                                        {/*    <p className="gallery-image-title">{image.title}</p>*/}
                                        {/*</div>*/}
                                    </div>
                                </div>
                                <div className="gallery-description">
                                    {/*<h4 className="gallery-description-title">{image.title}</h4>*/}
                                    <p className="gallery-description-text">{image.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {images.length > 0 && loadedImages < images.length && (
                        <div className="gallery-loading-more">
                            <p>Loading images... {loadedImages} of {images.length}</p>
                            <div className="gallery-progress">
                                <div
                                    className="gallery-progress-bar"
                                    style={{width: `${(loadedImages / images.length) * 100}%`}}
                                ></div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Lightbox Modal */}
            {selectedImage && (
                <div className="gallery-lightbox" onClick={closeLightbox}>
                    <div className="gallery-lightbox-content" onClick={e => e.stopPropagation()}>
                        <button
                            className="gallery-close-button"
                            onClick={closeLightbox}
                            aria-label="Close"
                        >
                            &times;
                        </button>

                        <div className="gallery-lightbox-image-container">
                            <img
                                src={selectedImage.url}
                                alt={selectedImage.title}
                                className="gallery-lightbox-image"
                            />
                        </div>

                        <div className="gallery-lightbox-info">
                            {/*<h3 className="gallery-lightbox-title">{selectedImage.title}</h3>*/}
                            <p className="gallery-lightbox-description">{selectedImage.description}</p>
                        </div>

                        <button
                            className="gallery-nav-button gallery-prev"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateImage('prev');
                            }}
                            aria-label="Previous image"
                        >
                            &#10094;
                        </button>

                        <button
                            className="gallery-nav-button gallery-next"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateImage('next');
                            }}
                            aria-label="Next image"
                        >
                            &#10095;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryComponent;